import {envIsIntegration} from '../config';
import {
  OrgType,
  RampIdType,
  useEntityFeatureFlagsQuery,
  useOrganizationFeatureFlagsQuery,
  useViewerFeatureFlagsQuery,
  useViewerOrganizationFeatureFlagsQuery,
} from '../generated/graphql';
import {propagateErrorsContext} from './errors';

// TODO: we should auto generate this list from ramp_settings.go's RampFeatures
// list. For now, we have to manually keep this list in sync.
// ALSO TODO: we should also have the server tell us the ramp key *type* so that the
// user just calls useRampFlag('disable-fuzzy-search') and we can figure out the type
// based on the RampSettings' IDType in ramp_settings.go
export type RampKey =
  | 'account-selector'
  | 'disable-fuzzy-search'
  | 'disable-query-merging'
  | 'gorilla-run-outliers'
  | 'gorilla-run-outliers-user'
  | 'global-registry'
  | 'large-workspace-mode-org'
  | 'large-workspace-mode-user'
  | 'no-keysets-org'
  | 'no-keysets-user'
  | 'run-selector-runs-query-org'
  | 'run-selector-runs-query-user'
  | 'sampled-runs-line-plot-notifications'
  | 'workspace-optimizations'
  | 'workspace-optimizations-by-user';

export const useEntityRampFlag = (
  entityName: string,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getEntityFlags: typeof useEntityFeatureFlagsQuery = useEntityFeatureFlagsQuery
) => {
  const {data} = getEntityFlags({
    variables: {teamName: entityName, rampIDType},
    skip: entityName == null,
  });
  // All features are enabled for CI
  if (envIsIntegration) {
    return true;
  }
  const teamFlagsGlobalList = data?.entity?.featureFlags;
  if (!teamFlagsGlobalList) {
    // disabling this until we have an actionable use for it. It's very noisy
    // console.warn('useEntityRampFlag: returning false due to no data');
    return false;
  }

  return (
    teamFlagsGlobalList.find(f => f?.rampKey === rampKey)?.isEnabled ?? false
  );
};

export const useUserRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getUserFlags: typeof useViewerFeatureFlagsQuery = useViewerFeatureFlagsQuery
) => {
  const {data} = getUserFlags({variables: {rampIDType}});
  if (!data?.viewer?.featureFlags) {
    // disabling this until we have an actionable use for it. It's very noisy
    // console.warn('useUserRampFlag: returning false due to no data');
    return false;
  }
  return (
    data.viewer.featureFlags.find(f => f?.rampKey === rampKey)?.isEnabled ??
    false
  );
};

export const useOrgRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getOrgFlags: typeof useViewerOrganizationFeatureFlagsQuery = useViewerOrganizationFeatureFlagsQuery
) => {
  const {data} = getOrgFlags({
    variables: {rampIDType},
  });
  if (!data?.viewer?.organizations) {
    // disabling this until we have an actionable use for it. It's very noisy
    // console.warn('useOrgRampFlag: returning false due to loading');
    return false;
  }

  const orgFlagIsEnabled = data.viewer.organizations.some(org =>
    org.featureFlags?.some(f => f?.rampKey === rampKey && f?.isEnabled)
  );
  return orgFlagIsEnabled;
};

export const useOrgNameRampFlag = (
  orgName: string,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getOrgFlags: typeof useOrganizationFeatureFlagsQuery = useOrganizationFeatureFlagsQuery
) => {
  const {data, loading} = getOrgFlags({
    variables: {orgName, rampIDType},
    skip: orgName == null,
    context: propagateErrorsContext(),
  });

  const orgFeatureFlags = data?.organization?.featureFlags;
  if (loading || orgFeatureFlags == null) {
    console.warn(
      'useOrganizationFeatureFlagsQuery: returning false due to no data'
    );
    return {data: false, loading};
  }
  if (data?.organization?.orgType === OrgType.Personal) {
    return {data: false, loading};
  }

  return {
    data: orgFeatureFlags.find(f => f?.rampKey === rampKey)?.isEnabled ?? false,
    loading,
  };
};
